import React from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { ChatAnalyticsDashboardResponse } from "./chatAnalyticsService";
import { SCOPE_COLORS } from "./chatAnalyticsPalette";

interface ChatScopeDonutProps {
  data: ChatAnalyticsDashboardResponse | null;
}

// Internal chat only ever runs over Google Chat — every other connected
// platform (Engati/Cloudee Chat Bot, Lazada, TikTok, Shopee...) talks to
// external end users, which is exactly the split the API already gives us
// as two separate score-card totals.
const ChatScopeDonut: React.FC<ChatScopeDonutProps> = ({ data }) => {
  const externalChats = data?.score_cards?.total_chats?.external || 0;
  const internalChats = data?.score_cards?.total_chats?.internal || 0;
  const total = externalChats + internalChats;

  if (total === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "300px" }}>
        <div className="text-center">
          <i className="ti ti-message-off fs-1 text-muted mb-3" />
          <p className="text-muted mb-0">No chats in this selection yet</p>
        </div>
      </div>
    );
  }

  const labels = ["External chats", "Internal chats (Google Chat)"];
  const series = [externalChats, internalChats];
  const colors = [SCOPE_COLORS.external, SCOPE_COLORS.internal];

  const options: ApexOptions = {
    chart: { type: "donut", height: 300, animations: { enabled: false } },
    labels,
    colors,
    fill: {
      type: "gradient",
      gradient: { shade: "light", type: "diagonal1", shadeIntensity: 0.3, opacityFrom: 1, opacityTo: 0.85 },
    },
    dataLabels: {
      enabled: true,
      style: { fontWeight: 700 },
      formatter: (val: number) => `${val.toFixed(0)}%`,
    },
    legend: { position: "bottom", fontWeight: 600, markers: { size: 7 } },
    stroke: { width: 3, colors: ["#fdfcff"] },
    plotOptions: {
      pie: {
        donut: {
          size: "66%",
          labels: {
            show: true,
            value: { fontSize: "26px", fontWeight: 700 },
            total: {
              show: true,
              label: "Total Chats",
              fontSize: "13px",
              formatter: () => String(total),
            },
          },
        },
      },
    },
    tooltip: { y: { formatter: (val: number) => `${val} chats` } },
  };

  return <Chart options={options} series={series} type="donut" height={300} />;
};

export default ChatScopeDonut;
